import styles from 'css/src/components/common/WorkspaceSelector.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';

import SelectWorkspaceRow from 'js/src/components/common/select-workspace-row';
import { BUTTON_COLORS, BUTTON_SIZES } from 'js/src/components/common/ui/Button';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput from 'js/src/components/common/ui/TextInput';
import { getMemberWorkspaceInfoList, selectActiveWorkspaceName } from 'js/src/data/account';
import Cookies from 'js-cookie';

type TogglePropsType = Readonly<Record<string, string>>;
type ItemPropsType = PlainDropdownItem<object> & { workspaceIdField: number };
type ItemPropsListType = readonly ItemPropsType[];

const WORKSPACE_ROW_ITEM_COMPONENT = SelectWorkspaceRow;

export const calculateToggleProps = (activeWorkspaceName: string): TogglePropsType => {
    return {
        text: activeWorkspaceName,
        iconRight: 'expand_more',
        color: BUTTON_COLORS.SIMPLE,
        size: BUTTON_SIZES.MD,
        className: styles['workspace-selector-toggle'],
    };
};

const showWorkspaceSelectorLoading = Cookies.get('show-workspace-selector-loading') === 'true';

// Minimum number of workspaces to show the search input
export const SEARCH_THRESHOLD = 5;

type SearchableMenuProps = {
    searchQuery?: string;
    onSearchChange?: (e: TypeInputChangeEvent) => void;
    showSearch?: boolean;
    children?: React.ReactNode;
};

export function SearchableMenu({
    searchQuery = '',
    onSearchChange,
    showSearch = false,
    children,
    ref,
    ...props
}: SearchableMenuProps & { ref?: React.Ref<HTMLDivElement> }) {
    return (
        <div ref={ref} className="dropdown-menu show" {...props}>
            {showSearch && (
                <div className="px-l5-8 pb-l5-8">
                    <TextInput
                        text={searchQuery}
                        onChange={onSearchChange}
                        placeholder="Search workspaces..."
                        iconLeft="search"
                        autoFocus
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </div>
            )}
            {children}
        </div>
    );
}

const WorkspaceSelector = React.memo(function WorkspaceSelector() {
    const [toggleProps, setToggleProps] = React.useState<TogglePropsType | null>(null);
    const [searchQuery, setSearchQuery] = React.useState('');
    const workspaces = useSelector(getMemberWorkspaceInfoList);
    const activeWorkspaceName = useSelector(selectActiveWorkspaceName);

    React.useEffect(() => {
        setToggleProps(calculateToggleProps(activeWorkspaceName));
    }, [activeWorkspaceName]);

    // Filter and sort workspaces based on search query
    const itemPropsList = React.useMemo((): ItemPropsListType => {
        const searchLower = searchQuery.toLowerCase().trim();

        // Filter workspaces by search query
        const filtered = searchLower
            ? workspaces.filter((ws) => ws.name?.toLowerCase().includes(searchLower))
            : workspaces;

        // Sort alphabetically by name
        const sorted = [...filtered].sort((a, b) => {
            const nameA = a.name || '';
            const nameB = b.name || '';
            return nameA.localeCompare(nameB);
        });

        // Convert to item props
        return sorted.map((ws) => ({
            id: ws.id.toString(),
            workspaceIdField: ws.id,
            itemComponent: WORKSPACE_ROW_ITEM_COMPONENT,
        }));
    }, [workspaces, searchQuery]);

    const showSearch = workspaces.length >= SEARCH_THRESHOLD;

    const handleSearchChange = React.useCallback((e: TypeInputChangeEvent) => {
        setSearchQuery(e.target.value);
    }, []);

    // Clear search when dropdown closes
    const handleAfterClose = React.useCallback(() => {
        setSearchQuery('');
    }, []);

    // Memoize menu props to avoid re-renders
    const menuProps = React.useMemo(
        () => ({
            searchQuery,
            onSearchChange: handleSearchChange,
            showSearch,
        }),
        [searchQuery, handleSearchChange, showSearch],
    );

    // still loading if workspaces is empty or toggleProps is not set
    const hasLoaded = workspaces.length > 0 && toggleProps !== null && activeWorkspaceName !== '';

    const shouldShow = workspaces.length > 1; // only show the dropdown if we have >1 workspace
    React.useEffect(() => {
        if (!hasLoaded) {
            return;
        }
        // if we should show, set a cookie so next time we'll show padding while loading
        Cookies.set('show-workspace-selector-loading', shouldShow ? 'true' : 'false');
    }, [hasLoaded, shouldShow]);

    if (!hasLoaded) {
        // if we haven't loaded workspaces yet, show a loading spacer if showWorkspaceSelectorLoading is true
        if (showWorkspaceSelectorLoading) {
            return <div style={{ height: '40px' }} />;
        } else {
            return null;
        }
    }

    if (!shouldShow) {
        return null;
    }

    return (
        <Dropdown
            limitMenuHeight
            toggleProps={toggleProps}
            items={itemPropsList}
            MenuComponent={SearchableMenu as React.ComponentType}
            menuProps={menuProps}
            afterClose={handleAfterClose}
        />
    );
});

export default WorkspaceSelector;
